import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronUp, Loader2, Plus, Trash2 } from 'lucide-react';
import BasketSelect from './BasketSelect';
import { useToast } from './Toast';
import { nurseBandingApi, positionApi, scheduleApi } from '../services/api';
import type {
  NurseBandBaseRateRowInput,
  NurseBandPositionRateCardInput,
  NurseBandScheduleAdditionalRowInput,
} from '../services/api';
import type { Position, Schedule } from '../types';

const BAND_YEAR_OPTIONS: { value: string; label: string }[] = [
  { value: 'any', label: 'Any year' },
  ...Array.from({ length: 7 }, (_, i) => ({ value: String(i), label: `Year ${i}` })),
];

function parseBandYear(value: string): number | null {
  return value === 'any' ? null : parseInt(value, 10);
}

function bandYearSelectValue(bandYear: number | null | undefined): string {
  return bandYear == null ? 'any' : String(bandYear);
}

function renumberBaseRows(rows: NurseBandBaseRateRowInput[]): NurseBandBaseRateRowInput[] {
  return rows.map((r, i) => ({ ...r, displayOrder: i }));
}

function renumberAdditionalRows(rows: NurseBandScheduleAdditionalRowInput[]): NurseBandScheduleAdditionalRowInput[] {
  return rows.map((r, i) => ({ ...r, displayOrder: i }));
}

function emptyBaseRow(): NurseBandBaseRateRowInput {
  return {
    bandYear: null,
    weekdayHourlyRate: 0,
    saturdayHourlyRate: 0,
    sundayHourlyRate: 0,
    bankHolidayHourlyRate: 0,
    midweekRedEyeHourlyRate: 0,
    weekendRedEyeHourlyRate: 0,
  };
}

function emptyRateCard(index: number): NurseBandPositionRateCardInput {
  return {
    name: `Rate table ${index + 1}`,
    positionIds: [],
    baseRateRows: [],
    scheduleAdditionalRows: [],
  };
}

function buildRateCardsFromApi(
  rateCards: Array<{ id: number; name: string; displayOrder: number; positionIds: number[] }>,
  baseRateRows: NurseBandBaseRateRowInput[],
  scheduleAdditionalRows: NurseBandScheduleAdditionalRowInput[],
): NurseBandPositionRateCardInput[] {
  if (rateCards.length === 0) return [emptyRateCard(0)];

  return rateCards.map((card) => ({
    id: card.id,
    name: card.name,
    displayOrder: card.displayOrder,
    positionIds: card.positionIds ?? [],
    baseRateRows: baseRateRows.filter((r) => r.rateCardId === card.id),
    scheduleAdditionalRows: scheduleAdditionalRows.filter((r) => r.rateCardId === card.id),
  }));
}

function getCardDisplayName(card: NurseBandPositionRateCardInput, index: number): string {
  return card.name.trim() || `Rate table ${index + 1}`;
}

const NurseBandingSettingsPanel: React.FC = () => {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(null);
  const [rateCards, setRateCards] = useState<NurseBandPositionRateCardInput[]>([emptyRateCard(0)]);

  const activePositions = useMemo(
    () => positions.filter((p) => p.isActive !== false),
    [positions],
  );

  const positionOptionsForSelectedCard = useMemo(() => {
    if (selectedCardIndex == null) return [];
    return activePositions
      .filter((position) => {
        const usedByOtherTable = rateCards.some(
          (card, index) => index !== selectedCardIndex && card.positionIds.includes(position.id),
        );
        return !usedByOtherTable;
      })
      .map((position) => ({ id: position.id, label: position.title }));
  }, [activePositions, rateCards, selectedCardIndex]);

  const load = async (showSpinner = true) => {
    try {
      if (showSpinner) setLoading(true);
      const [data, scheduleList, positionList] = await Promise.all([
        nurseBandingApi.getRateCards(),
        scheduleApi.getAll(),
        positionApi.getAll(),
      ]);
      setSchedules(scheduleList.filter((s) => s.isActive !== false));
      setPositions(positionList ?? []);
      setRateCards(
        buildRateCardsFromApi(data.rateCards ?? [], data.baseRateRows ?? [], data.scheduleAdditionalRows ?? []),
      );
      setSelectedCardIndex(null);
    } catch (err: any) {
      toastRef.current.error(err?.response?.data?.message || err?.message || 'Failed to load position banding settings.');
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const moveRow = <T,>(rows: T[], index: number, direction: 'up' | 'down'): T[] => {
    const next = [...rows];
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= next.length) return rows;
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  };

  const updateRateCard = (cardIndex: number, updater: (card: NurseBandPositionRateCardInput) => NurseBandPositionRateCardInput) => {
    setRateCards((cards) => {
      const next = [...cards];
      next[cardIndex] = updater(next[cardIndex]);
      return next;
    });
  };

  const addRateCard = () => {
    setRateCards((cards) => {
      const next = [...cards, emptyRateCard(cards.length)];
      setSelectedCardIndex(next.length - 1);
      return next;
    });
  };

  const removeRateCard = (cardIndex: number) => {
    setRateCards((cards) => {
      if (cards.length <= 1) return cards;
      const next = cards.filter((_, i) => i !== cardIndex);
      setSelectedCardIndex((current) => {
        if (current == null) return null;
        if (current === cardIndex) return null;
        if (current > cardIndex) return current - 1;
        return current;
      });
      return next;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payloadCards = rateCards.map((card, cardIndex) => ({
        ...card,
        name: card.name.trim() || `Rate table ${cardIndex + 1}`,
        displayOrder: cardIndex,
        baseRateRows: renumberBaseRows(card.baseRateRows),
        scheduleAdditionalRows: renumberAdditionalRows(card.scheduleAdditionalRows),
      }));
      await nurseBandingApi.saveRateCards({
        rateCards: payloadCards,
      });
      toast.success('Position banding rate tables saved.');
      await load(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const renderTableList = (options?: { compact?: boolean; showHeading?: boolean }) => {
    const compact = options?.compact ?? false;
    const showHeading = options?.showHeading ?? true;

    return (
      <div className={compact ? 'space-y-3' : 'space-y-4'}>
        {showHeading && (
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-neutral-900">Banding rate tables</h3>
              {!compact && (
                <p className="text-sm text-neutral-600 mt-1">Select a table to edit positions and rates.</p>
              )}
            </div>
            <button
              type="button"
              onClick={addRateCard}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex-shrink-0"
            >
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
        )}

        {rateCards.length === 0 ? (
          <p className="text-sm text-neutral-500">No rate tables yet. Add one to get started.</p>
        ) : (
          <ul className="divide-y divide-neutral-100 rounded-md border border-neutral-200 overflow-hidden">
            {rateCards.map((card, cardIndex) => {
              const positionCount = card.positionIds.length;
              const baseRowCount = card.baseRateRows.length;
              const isSelected = selectedCardIndex === cardIndex;
              return (
                <li key={`card-list-${cardIndex}`}>
                  <button
                    type="button"
                    onClick={() => setSelectedCardIndex(cardIndex)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-3 text-left transition-colors ${
                      isSelected
                        ? 'bg-blue-50 border-l-4 border-l-blue-600'
                        : 'hover:bg-neutral-50 border-l-4 border-l-transparent'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className={`font-medium truncate ${isSelected ? 'text-blue-900' : 'text-neutral-900'}`}>
                        {getCardDisplayName(card, cardIndex)}
                      </div>
                      <div className="text-xs text-neutral-500 mt-0.5">
                        {positionCount} position{positionCount === 1 ? '' : 's'} · {baseRowCount} row
                        {baseRowCount === 1 ? '' : 's'}
                      </div>
                    </div>
                    {!compact && (
                      <ChevronDown className="h-4 w-4 text-neutral-400 -rotate-90 flex-shrink-0 lg:hidden" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const renderTableEditor = (cardIndex: number, showBackButton: boolean) => {
    const card = rateCards[cardIndex];
    if (!card) return null;

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {showBackButton ? (
            <button
              type="button"
              onClick={() => setSelectedCardIndex(null)}
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
            >
              <ChevronLeft className="h-4 w-4" />
              All rate tables
            </button>
          ) : (
            <h3 className="font-semibold text-neutral-900">{getCardDisplayName(card, cardIndex)}</h3>
          )}
          {rateCards.length > 1 && (
            <button
              type="button"
              onClick={() => removeRateCard(cardIndex)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" /> Remove table
            </button>
          )}
        </div>

        <div className="space-y-4">
          <label className="block text-sm max-w-md">
            <span className="text-neutral-700 font-medium">Table name</span>
            <input
              type="text"
              className="mt-1 w-full rounded border border-neutral-300 px-3 py-2 text-sm"
              value={card.name}
              onChange={(e) => updateRateCard(cardIndex, (c) => ({ ...c, name: e.target.value }))}
              placeholder="e.g. Nurses, Care assistants"
            />
          </label>

          <BasketSelect
            label="Positions using this table"
            availableItems={positionOptionsForSelectedCard}
            selectedIds={card.positionIds}
            onChange={(ids) =>
              updateRateCard(cardIndex, (c) => ({
                ...c,
                positionIds: ids.map((id) => Number(id)).filter((id) => Number.isFinite(id)),
              }))
            }
            placeholder="Select positions..."
            required
          />
        </div>

        <div className="space-y-3 border-t border-neutral-100 pt-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h4 className="font-semibold text-neutral-900">Schedule / position additional rates</h4>
              <p className="text-sm text-neutral-600">
                Extra €/hr by band year, schedule, and/or position (e.g. supervisor premium when clocked as Nurse
                Supervisor on the same nurse rota).
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                updateRateCard(cardIndex, (c) => ({
                  ...c,
                  scheduleAdditionalRows: renumberAdditionalRows([
                    ...c.scheduleAdditionalRows,
                    { bandYear: null, scheduleId: null, positionId: null, additionalHourlyRate: 0 },
                  ]),
                }))
              }
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" /> Add row
            </button>
          </div>
          {card.scheduleAdditionalRows.length === 0 ? (
            <p className="text-sm text-neutral-500">No rows. Add rows to define schedule/position additional rates.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-neutral-600">
                    <th className="py-2 pr-2 w-16">Order</th>
                    <th className="py-2 pr-2">Year</th>
                    <th className="py-2 pr-2">Schedule</th>
                    <th className="py-2 pr-2">Position</th>
                    <th className="py-2 pr-2">Additional €/hr</th>
                    <th className="py-2 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {card.scheduleAdditionalRows.map((row, index) => (
                    <tr key={`add-${cardIndex}-${index}`} className="border-b border-neutral-100">
                      <td className="py-2 pr-2">
                        <div className="flex flex-col">
                          <button
                            type="button"
                            disabled={index === 0}
                            onClick={() =>
                              updateRateCard(cardIndex, (c) => ({
                                ...c,
                                scheduleAdditionalRows: renumberAdditionalRows(
                                  moveRow(c.scheduleAdditionalRows, index, 'up'),
                                ),
                              }))
                            }
                            className="p-0.5 disabled:opacity-30"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            disabled={index === card.scheduleAdditionalRows.length - 1}
                            onClick={() =>
                              updateRateCard(cardIndex, (c) => ({
                                ...c,
                                scheduleAdditionalRows: renumberAdditionalRows(
                                  moveRow(c.scheduleAdditionalRows, index, 'down'),
                                ),
                              }))
                            }
                            className="p-0.5 disabled:opacity-30"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="py-2 pr-2">
                        <select
                          className="w-full rounded border border-neutral-300 px-2 py-1.5"
                          value={bandYearSelectValue(row.bandYear)}
                          onChange={(e) => {
                            const v = parseBandYear(e.target.value);
                            updateRateCard(cardIndex, (c) => {
                              const rows = [...c.scheduleAdditionalRows];
                              rows[index] = { ...rows[index], bandYear: v };
                              return { ...c, scheduleAdditionalRows: rows };
                            });
                          }}
                        >
                          {BAND_YEAR_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 pr-2">
                        <select
                          className="w-full min-w-[180px] rounded border border-neutral-300 px-2 py-1.5"
                          value={row.scheduleId == null ? 'any' : String(row.scheduleId)}
                          onChange={(e) => {
                            const v = e.target.value === 'any' ? null : parseInt(e.target.value, 10);
                            updateRateCard(cardIndex, (c) => {
                              const rows = [...c.scheduleAdditionalRows];
                              rows[index] = { ...rows[index], scheduleId: v };
                              return { ...c, scheduleAdditionalRows: rows };
                            });
                          }}
                        >
                          <option value="any">Any schedule</option>
                          {schedules.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 pr-2">
                        <select
                          className="w-full min-w-[180px] rounded border border-neutral-300 px-2 py-1.5"
                          value={row.positionId == null ? 'any' : String(row.positionId)}
                          onChange={(e) => {
                            const v = e.target.value === 'any' ? null : parseInt(e.target.value, 10);
                            updateRateCard(cardIndex, (c) => {
                              const rows = [...c.scheduleAdditionalRows];
                              rows[index] = { ...rows[index], positionId: v };
                              return { ...c, scheduleAdditionalRows: rows };
                            });
                          }}
                        >
                          <option value="any">Any position</option>
                          {(card.positionIds.length > 0
                            ? positions.filter((p) => card.positionIds.includes(p.id))
                            : positions
                          ).map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.title}
                            </option>
                          ))}
                          {row.positionId != null
                            && !positions.some((p) => p.id === row.positionId)
                            && !card.positionIds.includes(row.positionId) ? (
                            <option value={row.positionId}>
                              {row.positionName || `Position ${row.positionId}`}
                            </option>
                          ) : null}
                        </select>
                      </td>
                      <td className="py-2 pr-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          required
                          className="w-28 rounded border border-neutral-300 px-2 py-1.5"
                          value={row.additionalHourlyRate}
                          onChange={(e) => {
                            updateRateCard(cardIndex, (c) => {
                              const rows = [...c.scheduleAdditionalRows];
                              rows[index] = {
                                ...rows[index],
                                additionalHourlyRate: parseFloat(e.target.value) || 0,
                              };
                              return { ...c, scheduleAdditionalRows: rows };
                            });
                          }}
                        />
                      </td>
                      <td className="py-2">
                        <button
                          type="button"
                          onClick={() =>
                            updateRateCard(cardIndex, (c) => ({
                              ...c,
                              scheduleAdditionalRows: renumberAdditionalRows(
                                c.scheduleAdditionalRows.filter((_, i) => i !== index),
                              ),
                            }))
                          }
                          className="text-red-600 p-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="space-y-3 border-t border-neutral-100 pt-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h4 className="font-semibold text-neutral-900">Base rates by year</h4>
              <p className="text-sm text-neutral-600">
                Mon–Fri, weekend, bank holiday, and overnight red-eye columns. Red-eye applies from 00:01 to shift end on overnight shifts.
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                updateRateCard(cardIndex, (c) => ({
                  ...c,
                  baseRateRows: renumberBaseRows([...c.baseRateRows, emptyBaseRow()]),
                }))
              }
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" /> Add row
            </button>
          </div>
          {card.baseRateRows.length === 0 ? (
            <p className="text-sm text-neutral-500">No rows. Add rows matching your banding grid.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-neutral-600">
                    <th className="py-2 pr-2 w-16">Order</th>
                    <th className="py-2 pr-2">Year</th>
                    <th className="py-2 pr-2">Mon–Fri</th>
                    <th className="py-2 pr-2">Saturday</th>
                    <th className="py-2 pr-2">Sunday</th>
                    <th className="py-2 pr-2">Bank holidays</th>
                    <th className="py-2 pr-2">Midweek red eye</th>
                    <th className="py-2 pr-2">Weekend red eye</th>
                    <th className="py-2 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {card.baseRateRows.map((row, index) => (
                    <tr key={`base-${cardIndex}-${index}`} className="border-b border-neutral-100">
                      <td className="py-2 pr-2">
                        <div className="flex flex-col">
                          <button
                            type="button"
                            disabled={index === 0}
                            onClick={() =>
                              updateRateCard(cardIndex, (c) => ({
                                ...c,
                                baseRateRows: renumberBaseRows(moveRow(c.baseRateRows, index, 'up')),
                              }))
                            }
                            className="p-0.5 disabled:opacity-30"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            disabled={index === card.baseRateRows.length - 1}
                            onClick={() =>
                              updateRateCard(cardIndex, (c) => ({
                                ...c,
                                baseRateRows: renumberBaseRows(moveRow(c.baseRateRows, index, 'down')),
                              }))
                            }
                            className="p-0.5 disabled:opacity-30"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="py-2 pr-2">
                        <select
                          className="w-full rounded border border-neutral-300 px-2 py-1.5"
                          value={bandYearSelectValue(row.bandYear)}
                          onChange={(e) => {
                            const v = parseBandYear(e.target.value);
                            updateRateCard(cardIndex, (c) => {
                              const rows = [...c.baseRateRows];
                              rows[index] = { ...rows[index], bandYear: v };
                              return { ...c, baseRateRows: rows };
                            });
                          }}
                        >
                          {BAND_YEAR_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      {(
                        [
                          'weekdayHourlyRate',
                          'saturdayHourlyRate',
                          'sundayHourlyRate',
                          'bankHolidayHourlyRate',
                          'midweekRedEyeHourlyRate',
                          'weekendRedEyeHourlyRate',
                        ] as const
                      ).map((field) => (
                        <td key={field} className="py-2 pr-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            required
                            className="w-24 rounded border border-neutral-300 px-2 py-1.5"
                            value={row[field]}
                            onChange={(e) => {
                              updateRateCard(cardIndex, (c) => {
                                const rows = [...c.baseRateRows];
                                rows[index] = { ...rows[index], [field]: parseFloat(e.target.value) || 0 };
                                return { ...c, baseRateRows: rows };
                              });
                            }}
                          />
                        </td>
                      ))}
                      <td className="py-2">
                        <button
                          type="button"
                          onClick={() =>
                            updateRateCard(cardIndex, (c) => ({
                              ...c,
                              baseRateRows: renumberBaseRows(c.baseRateRows.filter((_, i) => i !== index)),
                            }))
                          }
                          className="text-red-600 p-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-neutral-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading position banding…
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl md:text-2xl font-bold">Position Banding Payroll</h2>
        <p className="text-sm text-neutral-600 mt-1">
          Configure banding rate tables by position. Each table can cover one or more positions. Overnight shifts use
          midweek or weekend red-eye rates from 00:01 to shift end; earlier hours use the standard day-type columns.
        </p>
      </div>

      {/* Desktop: master-detail */}
      <div className="hidden lg:grid lg:grid-cols-[minmax(240px,280px)_minmax(0,1fr)] lg:gap-4 lg:items-start">
        <div className="rounded-lg border border-neutral-200 p-4 lg:sticky lg:top-4">
          {renderTableList({ compact: true, showHeading: true })}
        </div>
        <div className="rounded-lg border border-neutral-200 p-4 min-h-[420px]">
          {selectedCardIndex != null ? (
            renderTableEditor(selectedCardIndex, false)
          ) : (
            <div className="flex h-full min-h-[360px] items-center justify-center text-center px-6">
              <div>
                <p className="text-neutral-700 font-medium">Select a rate table</p>
                <p className="text-sm text-neutral-500 mt-1">
                  Choose a table on the left to edit positions and banding rates.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile: list or detail */}
      <div className="lg:hidden">
        {selectedCardIndex == null ? (
          <div className="rounded-lg border border-neutral-200 p-4">
            {renderTableList({ compact: false, showHeading: true })}
          </div>
        ) : (
          <div className="rounded-lg border border-neutral-200 p-4">
            {renderTableEditor(selectedCardIndex, true)}
          </div>
        )}
      </div>

      <div className="flex justify-end border-t border-neutral-100 pt-4">
        <button
          type="button"
          disabled={saving}
          onClick={handleSave}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save rate tables'}
        </button>
      </div>
    </div>
  );
};

export default NurseBandingSettingsPanel;
